import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PricingCard } from '../components/pricing/PricingCard';
import { ComparisonTable } from '../components/pricing/ComparisonTable';
export function WebDesign() {
  const [tableExpanded, setTableExpanded] = useState(false);
  const toggleTable = () => {
    setTableExpanded(!tableExpanded);
  };
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBronzeClick = () => {
    window.open('https://buy.stripe.com/aFa14hfmQ5ga8v85gsaZi00', '_blank');
  };
  const handleSilverClick = () => {
    window.open('https://buy.stripe.com/eVqcMZ3E8cICbHk4coaZi01', '_blank');
  };
  const handleGoldClick = () => {
    window.open('https://buy.stripe.com/eVq3cpfmQ6ke8v86kwaZi02', '_blank');
  };
  return <>
      <Helmet data-id="element-2126">
        <title data-id="element-2127">Website Design Packages UK | Bespoke Sites from £399</title>
        <meta name="description" content="Responsive, SEO-ready websites built in the UK. Up to 15 pages, branding and content options." data-id="element-2128" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-[clamp(56px,8vw,72px)] pb-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2129">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2130">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2131">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2132">
              Web Design Packages from £399 Inc VAT
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2133">
              Professional, mobile-optimized websites that deliver exceptional
              user experiences. Every site we create is built with performance,
              accessibility, and SEO best practices at its core.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Pricing Cards Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2134">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2135">
          <div className="max-w-7xl mx-auto" data-id="element-2136">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2137">
              <PricingCard title="Bronze" subtitle="Starter" price="£399" popular={false} features={['Up to 5 pages', 'Responsive design', 'Enquiry form', 'Blog option', 'Basic SEO', '1 business email']} iconSrc="https://images.unsplash.com/photo-1605478328994-f93e98217da7?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Bronze web design plan icon" ctaText="Get Started" onClick={handleBronzeClick} data-id="element-2138" />
              <PricingCard title="Silver" subtitle="Growing Business" price="£599" popular={true} features={['Up to 10 pages', 'Branding + favicon', '3 business emails', 'SEO setup', 'Domain + SSL (year 1)', '30-day support']} iconSrc="https://images.unsplash.com/photo-1568480289356-5a75d0fd47fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Silver web design plan icon" ctaText="Most Popular" onClick={handleSilverClick} data-id="element-2139" />
              <PricingCard title="Gold" subtitle="Full Service" price="£899" popular={false} features={['Up to 15 pages', 'Logo + trust badges', 'Advanced SEO', 'Payment links', 'Content writing included', 'GA + GSC setup']} iconSrc="https://images.unsplash.com/photo-1610375461369-d613b564f4c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Gold web design plan icon" ctaText="Get Started" onClick={handleGoldClick} data-id="element-2140" />
            </div>
            <div className="mt-12 text-center" data-id="element-2141">
              <button onClick={toggleTable} className="inline-flex items-center justify-center mx-auto px-8 py-3 rounded-full font-medium text-[15px] text-[#0DC3DC] bg-transparent border border-[#0DC3DC] border-opacity-40 hover:bg-gradient-to-r hover:from-[#0DC3DC] hover:to-[#00A8BF] hover:text-white hover:border-opacity-0 transition-all duration-300 shadow-sm hover:shadow-lg" style={{
              fontFamily: 'Poppins, sans-serif',
              minHeight: '46px'
            }} data-id="element-2142">
                <span className="mr-2" data-id="element-2143">
                  {tableExpanded ? 'Hide detailed comparison' : 'Show detailed comparison'}
                </span>
                {tableExpanded ? <ChevronUpIcon size={16} data-id="element-2144" /> : <ChevronDownIcon size={16} data-id="element-2145" />}
              </button>
            </div>
            {/* Comparison Table */}
            {tableExpanded && <ComparisonTable data-id="element-2146" />}
          </div>
        </div>
      </section>
      {/* Portfolio Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2147">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2148">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2149">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2150">
              Our Recent Projects
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2151">
              Take a look at some of our recent web design projects. We've
              helped businesses across various industries establish a strong
              online presence and achieve their goals.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2152">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2153">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pQKeoA3T9GzSKJQNGTdgoz/websites_1_-_ooOhmy.co.uk.jpg" alt="ooOhMy.co.uk e-commerce website - professional ladies fashion online store designed by Bright Pixxel Studios with modern layout and seamless shopping experience" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2154" />
              <div className="p-4 flex-grow" data-id="element-2155">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2156">ooOhMy.co.uk</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2157">
                  E-commerce / Ladies Fashion
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2158">
                  "Bright Pixxel transformed our online store with a beautiful,
                  user-friendly design that has increased our sales by 45%.
                  Their attention to detail and e-commerce expertise made all
                  the difference."
                </p>
                <a href="https://ooohmy.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2159">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2160">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d5GzTyf2oC8ykFDj3X9wmi/Website_3_-_northamptondrivingschool.co.uk.jpg" alt="Northampton Driving School website - professional driving school website with online booking system designed by Bright Pixxel Studios UK" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2161" />
              <div className="p-4 flex-grow" data-id="element-2162">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2163">
                  Northampton Driving School
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2164">
                  Education / Driving Lessons
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2165">
                  "Bookings increased by 60% since launching our new website.
                  The online booking system has streamlined our operations and
                  the mobile-friendly design has been perfect for our younger
                  students."
                </p>
                <a href="https://northamptondrivingschool.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2166">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2167">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4kYo3C4gd9QjfCrgk7a12h/Website_2_-_medicolegal-psychiatry.co.uk.jpg" alt="Medicolegal Psychiatry website - professional healthcare psychiatry practice website designed by Bright Pixxel Studios with patient-focused design" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2168" />
              <div className="p-4 flex-grow" data-id="element-2169">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2170">
                  Medicolegal Partners
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2171">
                  Healthcare / Psychiatry
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2172">
                  "The professional site perfectly represents our practice.
                  Bright Pixxel understood our need for a sophisticated,
                  trustworthy online presence that respects patient
                  confidentiality while being informative."
                </p>
                <a href="https://medicolegal-psychiatry.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2173">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2174">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2mYs5H4cmXngmP57UGoyEE/Website_4_-_surefix.it.com.jpg" alt="SureFix.it appliance repair website - professional home appliance repair service website with online booking designed by Bright Pixxel Studios" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2175" />
              <div className="p-4 flex-grow" data-id="element-2176">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2177">SureFix.it</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2178">
                  Home Services / Appliance Repair
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2179">
                  "Lead generation increased 75% year-on-year after our website
                  redesign. The online booking system and service area map have
                  dramatically improved customer experience and our conversion
                  rate."
                </p>
                <a href="https://surefix.it.com" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2180">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2181">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wqw4YWP28GPXqhAPXrrgAj/Website_5_-_axiscare.it.com.jpg" alt="AxisCare appliance repair website - professional appliance repair service website with SEO optimization designed by Bright Pixxel Studios UK" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2182" />
              <div className="p-4 flex-grow" data-id="element-2183">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2184">AxisCare.it</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2185">
                  Home Services / Appliance Repair
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2186">
                  "ROI on our website in just 2 months! The SEO work has put us
                  on the first page of Google for our key service areas. Bright
                  Pixxel delivered exactly what we needed to grow our repair
                  business."
                </p>
                <a href="https://axiscare.it.com" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2187">
                  Visit website →
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2188">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2189">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2190">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mb-8" data-id="element-2191">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio - Affordable, Creative, Digital Excellence" className="h-16 mx-auto" data-id="element-2192" />
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2193">
              Need a custom package?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2194">
              We understand that every business is unique. If our standard
              packages don't quite fit your needs, we'd be happy to create a
              custom solution tailored specifically for you.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-2195">
              <Button size="lg" onClick={scrollToContact} data-id="element-2196">
                Contact Us Today
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}